import useTranslation from 'next-translate/useTranslation'
import clsx from 'clsx'
import GlobeIcon from '@ancon/wildcat-ui/shared/icons/globe-outline.svg'
import CheckIcon from '@ancon/wildcat-ui/shared/icons/check.svg'

import DropdownIcon from '../../../../app/components/DropdownIcon'
import PopoverPanel from '../../../../app/components/popover/PopoverPanel'
import LanguageOptionLabel from '../../../../app/components/language/LanguageOptionLabel'
import usePopover from '../../../../app/hooks/usePopover'
import { AppTestIds, LanguageOptions } from '../../../../app/constants'

import styles from './NavLanguageMenu.module.scss'

type NavLanguageMenuProps = {
  language: string
  onChange: (language: string) => void
}

/** Language picker: a pill in the top bar and a short list */
export default function NavLanguageMenu({
  language,
  onChange,
}: NavLanguageMenuProps) {
  const { t } = useTranslation('common')
  const { isOpen, close, containerRef, triggerRef, triggerProps, popoverId } =
    usePopover()

  function handleSelect(value: string) {
    close()

    if (value !== language) {
      onChange(value)
    }
  }

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        className={clsx(styles.trigger, { [styles.isOpen]: isOpen })}
        // Starts with the visible code so voice control users can say it
        aria-label={t('components.navBar.languageMenuLabel', {
          language: language.toUpperCase(),
        })}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...triggerProps}
      >
        <GlobeIcon className={styles.globe} aria-hidden />
        <span className={styles.code}>{language}</span>
        <DropdownIcon isOpen={isOpen} />
      </button>
      {isOpen && (
        <PopoverPanel id={popoverId} className={styles.panel}>
          <ul className={styles.options}>
            {LanguageOptions.map(option => {
              const isSelected = option.value === language

              return (
                <li key={option.value}>
                  <button
                    type="button"
                    className={clsx(styles.option, {
                      [styles.isSelected]: isSelected,
                    })}
                    aria-pressed={isSelected}
                    lang={option.value}
                    onClick={() => handleSelect(option.value)}
                    data-testid={
                      AppTestIds.NavBar.Dropdown.Option + option.value
                    }
                  >
                    <LanguageOptionLabel flag={option.flag}>
                      {option.label}
                    </LanguageOptionLabel>
                    {isSelected && (
                      <CheckIcon className={styles.check} aria-hidden />
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        </PopoverPanel>
      )}
    </div>
  )
}
